import styled from '@emotion/styled';
import { Modal, Table } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { AmountInPattern, decryptToken } from '../../utils/common_functions';
import { FlexWrapper } from '../../theme/common_style';
import { ArrowLeft, ArrowRight } from '../../theme/SvgIcons';
import { amountKeys, dateKeys, milestoneStatusOption, statusKeys } from '../../utils/constant';
import dayjs from 'dayjs';

const status = {
  Milestone: milestoneStatusOption
};

const arr = ['Milestone'];

const ViewActivityAction = ({ open, onCancel, data, exclude = [], secureKey = [] }) => {
  const [tableData, setTableData] = useState([]);
  const [arrowIndex, setArrowIndex] = useState(0);
  const actionType = ['Update'];

  const handleColumnValue = (text, record, type) => {
    if (Array.isArray(text)) {
      return (
        <IsChangedStyle changed={record?.changed} type={type}>
          {text?.join(', ') || 'N/A'}
        </IsChangedStyle>
      );
    } else {
      return (
        <IsChangedStyle changed={record?.changed} type={type}>
          {text !== undefined && text !== null && text !== '' ? text : 'N/A'}
        </IsChangedStyle>
      );
    }
  };

  const columns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      render: (text) => <div style={{ fontWeight: 500 }}>{text}</div>
    },
    {
      title: 'Old Value',
      dataIndex: 'oldValue',
      key: 'oldValue',
      render: (text, record) => handleColumnValue(text, record, 'old')
    },
    ...(actionType?.includes(data?.action_type)
      ? [
          {
            title: 'New Value',
            dataIndex: 'newValue',
            key: 'newValue',
            render: (text, record) => handleColumnValue(text, record, 'new')
          }
        ]
      : [])
  ];

  const manageTableData = (oldVal, newVal) => {
    const flattenObject = (obj, parentKey = '') => {
      let result = {};

      for (let key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const value = obj[key];

        const baseKey = key.split('.')[0];
        if (exclude.includes(key) || exclude.includes(baseKey)) continue;

        if (value === null || value === undefined) {
          result[fullKey] = '';
        } else if (Array.isArray(value)) {
          if (
            value.every((item) => typeof item === 'object' && item !== null && !Array.isArray(item))
          ) {
            value.forEach((item, index) => {
              const nested = flattenObject(item, `${fullKey}[${index}]`, exclude);
              Object.assign(result, nested);
            });
          } else {
            result[fullKey] = value;
          }
        } else if (typeof value === 'object') {
          const nested = flattenObject(value, fullKey, exclude);
          Object.assign(result, nested);
        } else {
          const currentActive = arr?.find((el) => data?.action?.includes(el));

          if (dateKeys.includes(key) && value) {
            result[fullKey] = dayjs(value).format('YYYY-MM-DD');
          } else if (amountKeys.includes(key) && value) {
            result[fullKey] = `$${AmountInPattern(value || 0)}`;
          } else if (statusKeys.includes(key) && currentActive && status[currentActive]) {
            result[fullKey] = status[currentActive]?.find((el) => el?.id === value)?.name;
          } else {
            result[fullKey] = value;
          }
        }
      }

      return result;
    };

    const flattenedOld = flattenObject(oldVal);
    const flattenedNew = flattenObject(newVal);

    const mergedKeys = Array.from(
      new Set([...Object.keys(flattenedOld), ...Object.keys(flattenedNew)])
    ).filter((key) => {
      if (key?.includes('.')) {
        let childKey = key.split('.').pop();
        return !exclude.includes(childKey);
      } else {
        return !exclude.includes(key.split('.')[0]);
      }
    });

    const formatValue = (val, key) => {
      if (val === null || val === undefined) return '';
      if (Array.isArray(val)) return val;
      if (typeof val === 'object') return JSON.stringify(val);
      return secureKey.includes(key) ? String(decryptToken(val)) : String(val);
    };

    const isEqual = (a, b) => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return JSON.stringify(a) === JSON.stringify(b);
      }
      return a === b;
    };

    const formattedTableData = mergedKeys.map((key) => {
      const oldRaw = flattenedOld[key];
      const newRaw = flattenedNew[key];

      const oldFormatted = formatValue(oldRaw, key);
      const newFormatted = formatValue(newRaw, key);

      const lastKeySegment = key.split('.').pop();

      return {
        key,
        field: lastKeySegment.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        oldValue: oldFormatted,
        newValue: newFormatted,
        changed: !isEqual(oldFormatted, newFormatted)
      };
    });

    setTableData((pre) => [...pre, formattedTableData]);
  };

  useEffect(() => {
    if (data) {
      if (Array.isArray(data?.oldValue) || Array.isArray(data?.newValue)) {
        const len = data?.oldValue?.length || data?.newValue?.length;
        Array.from({ length: len }).forEach((_, index) => {
          manageTableData(data?.oldValue[index] || {}, data?.newValue[index] || {});
        });
      } else {
        manageTableData(data?.oldValue || {}, data?.newValue || {});
      }
    }
  }, [data]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      prefixCls="view-activity-modal"
      width={actionType?.includes(data?.action_type) ? '80%' : '800px'}
      footer={null}>
      <ViewLogStyle>
        <h2>Activity Log</h2>

        <FlexWrapper justify="space-between" align="center">
          <div className="activity-log">
            <span className="bold">{data?.action_type}d by</span>
            <span>{data?.user}</span>
            <span className="bold"> on</span>
            <span>
              {data?.date}, {data?.time}
            </span>
          </div>
          <FlexWrapper gap="5px" align="center">
            {tableData.length > 1 && (
              <button
                className="iconBtn"
                disabled={arrowIndex === 0}
                onClick={() => arrowIndex > 0 && setArrowIndex((pre) => pre - 1)}>
                <ArrowLeft />
              </button>
            )}
            {tableData.length > 1 && (
              <button
                className="iconBtn"
                disabled={arrowIndex === tableData.length - 1}
                onClick={() =>
                  arrowIndex < tableData.length - 1 && setArrowIndex((pre) => pre + 1)
                }>
                <ArrowRight />
              </button>
            )}
          </FlexWrapper>
        </FlexWrapper>

        <Table
          prefixCls="activity-log-table"
          columns={columns}
          dataSource={tableData[arrowIndex]}
          pagination={false}
          scroll={{ y: 'calc(100vh - 250px)' }}
          size="middle"
          bordered
          rowClassName={(record) =>
            actionType?.includes(data?.action_type) && record.changed ? 'row-changed' : ''
          }
          key={arrowIndex}
        />
      </ViewLogStyle>
    </Modal>
  );
};

export default ViewActivityAction;

ViewActivityAction.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  data: PropTypes.any,
  exclude: PropTypes.array,
  secureKey: PropTypes.array
};

const ViewLogStyle = styled.div`
  h2 {
    margin: 0;
    text-align: center;
  }

  .iconBtn {
    cursor: pointer;
    border: none;
    padding: 0;
    width: 28px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
  }

  .activity-log {
    display: flex;
    gap: 5px;
    margin: 20px 0 10px;
    font-size: 15px;

    .bold {
      font-weight: 500;
    }
  }

  .row-changed {
    background-color: #f6ffed;
  }

  .ant-table-cell {
    padding: 10px 12px;
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    font-weight: 600;
    background-color: #fafafa;
  }
`;

const IsChangedStyle = styled.span`
  color: ${({ changed, type }) => (changed ? (type === 'old' ? 'red' : 'green') : '#000')};
  font-weight: ${({ changed }) => (changed ? 500 : 400)};
  word-break: break-all;
`;
